import { create } from 'zustand';
import * as z from 'zod';

/**
 * Typed modal registry. Features augment this interface via declaration
 * merging to register their modal id → payload mapping, e.g.
 *
 * ```ts
 * declare module '@noor/lib' {
 *   interface ModalRegistry {
 *     'edit-order': { orderId: string };
 *   }
 * }
 * ```
 *
 * This keeps `shared` decoupled from `features` (no imports) while giving
 * `openModal('edit-order', { orderId })` full compile-time payload safety.
 * The built-in `confirm` modal is declared here since its host lives in `app`.
 */
export interface ModalRegistry {
  confirm: ConfirmModalPayload;
}

/**
 * Runtime schema for the confirm payload — the single source of truth (the
 * `ConfirmModalPayload` type is inferred from it). The `onResolve` callback
 * can't be JSON-validated, so it is checked structurally with `z.custom`.
 */
export const confirmModalPayloadSchema = z.object({
  title: z.string(),
  description: z.string().optional(),
  confirmLabel: z.string().optional(),
  cancelLabel: z.string().optional(),
  /** Marks the confirm action destructive (red button). */
  destructive: z.boolean().optional(),
  /**
   * Which illustration/typography the dialog wears. `warning` (default) is the
   * warning-octagon prompt used across the app; `courier` is the scooter-headed
   * handoff prompt from the Eats orders design.
   */
  variant: z.enum(['warning', 'courier']).optional(),
  /**
   * Optional async work to run from the confirm button. When present, the app
   * modal keeps itself open and busy until this settles successfully.
   */
  onConfirm: z
    .custom<() => unknown>((v) => typeof v === 'function', {
      message: 'onConfirm must be a function',
    })
    .optional(),
  /** Resolves the `confirmDialog()` promise. Wired by the app host. */
  onResolve: z.custom<(confirmed: boolean) => void>((v) => typeof v === 'function', {
    message: 'onResolve must be a function',
  }),
});

export type ConfirmModalPayload = z.infer<typeof confirmModalPayloadSchema>;

/** Any registered modal id. */
export type ModalId = keyof ModalRegistry;

/**
 * A single open modal instance. Distributed over `ModalId` so `id`
 * discriminates `payload` — narrowing on `id` narrows the payload type.
 */
export type ModalInstance<Id extends ModalId = ModalId> = Id extends ModalId
  ? {
      /** Unique per open() call, so the same id can stack/animate independently. */
      key: string;
      id: Id;
      payload: ModalRegistry[Id];
    }
  : never;

/**
 * Payload argument is optional only when the modal's payload type allows
 * `undefined`; otherwise it is required. Keeps `openModal('x')` legal for
 * payload-less modals and a type error for ones that need data.
 */
type OpenArgs<Id extends ModalId> = undefined extends ModalRegistry[Id]
  ? [id: Id, payload?: ModalRegistry[Id]]
  : [id: Id, payload: ModalRegistry[Id]];

interface ModalState {
  /** Open modals, bottom → top. The last entry is the topmost. */
  stack: ModalInstance[];
  /** Open a modal (pushes onto the stack). Returns the instance key. */
  openModal: <Id extends ModalId>(...args: OpenArgs<Id>) => string;
  /** Close the topmost modal, or a specific one by key or id. */
  closeModal: (keyOrId?: string) => void;
  /** Replace the entire stack with a single modal. */
  replaceModal: <Id extends ModalId>(...args: OpenArgs<Id>) => string;
  /** Close every open modal. */
  closeAll: () => void;
}

// Deterministic key generator (no Math.random — keeps tests/SSR stable).
let modalCounter = 0;
function nextKey(id: string): string {
  modalCounter += 1;
  return `${id}:${modalCounter}`;
}

export const useModalStore = create<ModalState>((set, get) => ({
  stack: [],

  openModal: (...args) => {
    const [id, payload] = args;
    const key = nextKey(id);
    set((state) => ({
      stack: [...state.stack, { key, id, payload } as ModalInstance],
    }));
    return key;
  },

  replaceModal: (...args) => {
    const [id, payload] = args;
    const key = nextKey(id);
    set({ stack: [{ key, id, payload } as ModalInstance] });
    return key;
  },

  closeModal: (keyOrId) => {
    const { stack } = get();
    if (stack.length === 0) return;
    if (keyOrId === undefined) {
      set({ stack: stack.slice(0, -1) });
      return;
    }
    set({
      stack: stack.filter((m) => m.key !== keyOrId && m.id !== keyOrId),
    });
  },

  closeAll: () => set({ stack: [] }),
}));

/* ------------------------------- Selectors ------------------------------- */

/** Imperative access (outside React, e.g. confirmDialog helper). */
export const modalStore = {
  open: <Id extends ModalId>(...args: OpenArgs<Id>): string =>
    useModalStore.getState().openModal<Id>(...args),
  close: (keyOrId?: string): void => useModalStore.getState().closeModal(keyOrId),
  closeAll: (): void => useModalStore.getState().closeAll(),
};

/** Subscribe to whether a given modal id is currently open. */
export function useIsModalOpen(id: ModalId): boolean {
  return useModalStore((s) => s.stack.some((m) => m.id === id));
}
